/**
 * Gate Out / Gate In — the physical-movement dialog.
 *
 * Confirming records the crossing: the time, the vehicle and the carrier. It moves
 * no stock in either direction, so there is no consequence to warn about here —
 * the alerts that used to count the lines about to post are gone with the posting.
 */
import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import InvButton from '../InvButton';
import { InvTextField } from '../InvField';
import { IPD_COLORS } from '../../theme';

/** datetime-local wants 'YYYY-MM-DDTHH:mm' and nothing else. */
const LOCAL_DATETIME = 'YYYY-MM-DDTHH:mm';

const EMPTY = { gate_out_at: '', vehicle_no: '', carrier_name: '', carrier_contact: '', remarks: '' };

export default function GateOutDialog({
  open,
  gatePass,
  mode = 'gate-out',
  loading = false,
  onConfirm,
  onCancel,
}) {
  const [form, setForm] = useState(EMPTY);

  // Seed from the pass — the carrier is usually already known at draft time, and
  // retyping a vehicle number at the gate is how wrong ones get entered.
  // The time defaults to now and stays editable: the entry is often made after
  // the vehicle has already gone, and "now" would then be the wrong record.
  useEffect(() => {
    if (!open) return;
    setForm({
      gate_out_at: dayjs().format(LOCAL_DATETIME),
      vehicle_no: gatePass?.vehicle_no || '',
      carrier_name: gatePass?.carrier_name || '',
      carrier_contact: gatePass?.carrier_contact || '',
      remarks: '',
    });
  }, [open, gatePass]);

  const set = (key) => (e) => setForm(prev => ({ ...prev, [key]: e.target.value }));

  const inward = mode === 'gate-in';

  /**
   * Gate-in takes remarks only, so only the outward path carries a timestamp.
   * The future check mirrors GATE_OUT_CLOCK_SKEW_MINUTES in gate_pass_service —
   * the server refuses it too, this just says so before the round trip.
   */
  function submit() {
    if (inward) {
      onConfirm({ remarks: form.remarks });
      return;
    }
    const when = dayjs(form.gate_out_at);
    if (!form.gate_out_at || !when.isValid()) {
      toast.error('Enter the date and time the material left');
      return;
    }
    if (when.isAfter(dayjs().add(5, 'minute'))) {
      toast.error('Gate out time cannot be in the future');
      return;
    }
    onConfirm({ ...form, gate_out_at: when.toISOString() });
  }

  const title = inward
    ? 'Record Gate In'
    : mode === 'approve-and-gate-out'
      ? 'Approve & Record Gate Out'
      : 'Record Gate Out';

  return (
    <Dialog open={open} onClose={loading ? undefined : onCancel} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontSize: 16, fontWeight: 600, color: IPD_COLORS.textPrimary }}>
        {title}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ color: IPD_COLORS.textSecondary, mb: 2 }}>
          {gatePass?.gate_pass_number}
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Gate-in takes remarks only. GatePassGateInRequest has no carrier fields —
              the inward document already carries its own, and rendering inputs whose
              values the server drops is worse than not asking. */}
          {!inward && (
            <>
              <InvTextField
                label="Gate out date & time *"
                type="datetime-local"
                value={form.gate_out_at}
                onChange={set('gate_out_at')}
                InputLabelProps={{ shrink: true }}
                helperText="When the material physically left the hospital premises."
              />
              <InvTextField
                label="Vehicle no."
                value={form.vehicle_no}
                onChange={set('vehicle_no')}
                placeholder="MH12AB1234"
              />
              <InvTextField
                label="Carrier name"
                value={form.carrier_name}
                onChange={set('carrier_name')}
              />
              <InvTextField
                label="Carrier contact"
                value={form.carrier_contact}
                onChange={set('carrier_contact')}
              />
            </>
          )}
          <InvTextField
            label="Remarks"
            value={form.remarks}
            onChange={set('remarks')}
            multiline
            minRows={2}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <InvButton variant="outlined" size="small" onClick={onCancel} disabled={loading}>
          Cancel
        </InvButton>
        <InvButton size="small" onClick={submit} disabled={loading}>
          {loading ? 'Saving…' : inward ? 'Record Gate In' : 'Record Gate Out'}
        </InvButton>
      </DialogActions>
    </Dialog>
  );
}
